import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { faSpinnerThird, faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Form from 'components/form';
import { InputCurrencyIcon } from 'components/input';
import { useBids } from 'components/pages/campaign-settings/campaign-auction/item-details/bids/useBids';
import { didItemEnd } from 'components/pages/campaign-settings/campaign-auction/util';
import * as Select from 'components/select';
import * as Table from 'components/table';
import * as Tooltip from 'components/tooltip';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { TransactionPaymentMethod } from 'types/domain';

interface BidderItem {
    id: number;
    name: string;
    number: number;
    payment_methods: TransactionPaymentMethod[];
}

const Bids = () => {
    const { auctionsManualBid, auctionsManageBidder } = useFlags();
    const { item, table, bidders, formMethods, onSubmitManualBid, isCreatingBid } = useBids();
    const {
        control,
        formState: { errors },
        path,
    } = formMethods;
    const showBidsTable = didItemEnd(item) || !item.can_delete;

    return (
        (auctionsManualBid || showBidsTable) && (
            <div className={'flex flex-col gap-y-4'}>
                <p className={'text-2xl font-medium'}>Bidders</p>
                {auctionsManualBid && !!item.allows_bid && item.dashboard_status === 'live' && (
                    <Form.Provider {...formMethods}>
                        <form onSubmit={onSubmitManualBid}>
                            <div
                                className={`flex justify-between gap-x-4 ${
                                    Object.keys(errors).length > 0 ? 'items-center' : 'items-end'
                                }`}
                            >
                                <div className={'flex gap-x-4'}>
                                    <div className={'flex flex-col gap-y-2'}>
                                        <Form.Label
                                            name={path('amount')}
                                            className={'text-sm font-medium'}
                                        >
                                            Bid amount
                                        </Form.Label>
                                        <Form.TextInputRoot
                                            name={'amount'}
                                            control={control}
                                            disabled={isCreatingBid}
                                            rules={{
                                                required: 'Amount is required',
                                            }}
                                            inputRootProps={{
                                                mask: 'decimal',
                                            }}
                                        >
                                            <InputCurrencyIcon />
                                            <Form.TextInputForm placeholder={'0.00'} />
                                        </Form.TextInputRoot>
                                        <Form.Error name={path('amount')} />
                                    </div>
                                    <div className={'flex flex-col gap-y-2'}>
                                        <Form.Label
                                            name={path('bidder')}
                                            className={'text-sm font-medium'}
                                        >
                                            Bidder name or ID
                                        </Form.Label>
                                        <Select.UseFormSearchableSelect
                                            disabled={isCreatingBid}
                                            name={'bidder'}
                                            control={control}
                                            inputProps={{
                                                placeholder: 'Bidder Name or ID #',
                                            }}
                                            isSearchableById={true}
                                            multi={false}
                                            rules={{
                                                required: 'Please select a bidder',
                                            }}
                                        >
                                            {bidders.map((bidder: BidderItem) => {
                                                return (
                                                    <Select.ListItem
                                                        key={bidder.id}
                                                        value={{
                                                            id: bidder.number.toString(),
                                                            label: `${bidder.number} - ${bidder.name}`,
                                                            value: bidder.number,
                                                        }}
                                                    >
                                                        {bidder.number} - {bidder.name}
                                                        {auctionsManageBidder &&
                                                            bidder.payment_methods?.length ===
                                                                0 && (
                                                                <Tooltip.Root>
                                                                    <Tooltip.Trigger>
                                                                        <span className="sr-only">
                                                                            Alert
                                                                        </span>
                                                                        <FontAwesomeIcon
                                                                            icon={
                                                                                faTriangleExclamation
                                                                            }
                                                                            className="text-yellow-500"
                                                                        />
                                                                    </Tooltip.Trigger>
                                                                    <Tooltip.Portal>
                                                                        <Tooltip.Content
                                                                            side="right"
                                                                            align="center"
                                                                        >
                                                                            <Tooltip.Arrow />
                                                                            <p>
                                                                                Needs payment
                                                                                information
                                                                            </p>
                                                                        </Tooltip.Content>
                                                                    </Tooltip.Portal>
                                                                </Tooltip.Root>
                                                            )}
                                                    </Select.ListItem>
                                                );
                                            })}
                                        </Select.UseFormSearchableSelect>
                                        <Form.Error name={path('bidder')} />
                                    </div>
                                </div>
                                <Button variant="solid" disabled={isCreatingBid} type={'submit'}>
                                    {isCreatingBid && (
                                        <FontAwesomeIcon
                                            className={'animate-spin'}
                                            icon={faSpinnerThird}
                                        />
                                    )}
                                    <p>Manually add bid</p>
                                </Button>
                            </div>
                        </form>
                    </Form.Provider>
                )}
                {showBidsTable && (
                    <Table.Root table={table} variant="bordered">
                        <Box className="overflow-x-auto">
                            <Table.Content>
                                <Table.Header />
                                <Table.Body />
                            </Table.Content>
                            <Table.Empty variant={'secondary'}>
                                <Table.EmptyIcon />
                                <Table.EmptyTitle>There are no bids on this item.</Table.EmptyTitle>
                            </Table.Empty>
                        </Box>
                        <Table.Pagination>
                            <Table.PaginationPageNavigation />
                        </Table.Pagination>
                    </Table.Root>
                )}
            </div>
        )
    );
};

export { Bids };
